import {observer} from 'mobx-react-lite'
import React, {useEffect, useState} from 'react'
import {Lang} from 'yougile-lang'
import {logger, Time, Util} from 'yougile-util'
import {UiSlot} from '@api/components'
import {Storage} from '@client/storage'
import {Tracking, TrackingEvent} from '@common/tracking'
import {Billing} from '@components/billing/billing'
import {BoardViewContext, boardViewStore} from '@components/board-view'
import {ErrorBoundary} from '@components/common/error-boundary/error-boundary'
import {pendingCompaniesAdminStore} from '@components/dashboard/company-users/pending-companies-admin-store'
import {DnDContext, DnDStore} from '@components/draggable'
import {FeedbackPage} from '@components/feedback-page'
import {Background} from '@components/logged-in/background'
import {MainMenu} from '@components/main-menu/main-menu'
import {
  MainMenuContext,
  MainMenuStore
} from '@components/main-menu/main-menu-store'
import {PageContainer} from '@components/page/page-container'
import {PartnerPage} from '@components/partner-page/partner-page'
import {PopupContainer} from '@components/popup-container/popup-container'
import {Project} from '@components/project'
import {
  RightPanel,
  RightPanelContext,
  rightPanelStore
} from '@components/right-panel/right-panel'
import {Scripts} from '@components/scripts'
import {Statistics} from '@components/statistics/statistics'
import {taskConnectionStore} from '@components/task-card/task-connection-store'
import {UserCard} from '@components/user-card/user-card'
import {useGlobalKeyDown, useLocationUpdateEffect} from '@containers/hooks'
import {cn} from '@containers/html-utils'
import {useCompanyUpdateEffect} from '@containers/update-hooks'
import {ChatUtils} from '@extensions/chat-utils'
import {globals} from '@extensions/globals-base'
import {idTasksStore} from '@extensions/id-tasks/id-tasks-store'
import {crmStore} from '../crm/crm-store'
import {
  LoggedInContext,
  LoggedInStore,
  useLoggedInStore
} from './logged-in-store'
import {PendingCompanyDialog} from './pending-company-dialog'

export const LoggedIn = observer(() => {
  const [mainMenuStore, setMainMenuStore] = useState<MainMenuStore>()
  const [loggedInStore, setLoggedInStore] = useState<LoggedInStore>()
  const [dndStore, setDndStore] = useState<DnDStore>()

  useEffect(() => {
    const event = Util.isDesktop()
      ? TrackingEvent.statsFrontPageIsOpenInDesktop
      : TrackingEvent.statsFrontPageIsOpenInBrowser
    const sendOnlineEvent = () => {
      Tracking.send(event)
    }
    const interval = setInterval(sendOnlineEvent, Time.hour * 4)
    sendOnlineEvent()

    return () => clearInterval(interval)
  }, [])

  useEffect(() => {
    const mainMenuStore = new MainMenuStore()
    setMainMenuStore(mainMenuStore)
    const loggedInStore = new LoggedInStore(mainMenuStore)
    setLoggedInStore(loggedInStore)
    const dndStore = new DnDStore()
    setDndStore(dndStore)
    crmStore.addCrmProjectListeners()

    return () => {
      pendingCompaniesAdminStore.dispose()
      dndStore.dispose()
      loggedInStore.dispose()
      crmStore.dispose()
    }
  }, [])

  if (!(loggedInStore && dndStore && mainMenuStore)) {
    return null
  }

  return (
    <ErrorBoundary
      silent
      onError={e => logger.error('logged-in error ' + e.message)}
    >
      <LoggedInContext.Provider value={loggedInStore}>
        <DnDContext.Provider value={dndStore}>
          <MainMenuContext.Provider value={mainMenuStore}>
            <RightPanelContext.Provider value={rightPanelStore}>
              <BoardViewContext.Provider value={boardViewStore}>
                <LoggedInBase />
              </BoardViewContext.Provider>
            </RightPanelContext.Provider>
          </MainMenuContext.Provider>
        </DnDContext.Provider>
      </LoggedInContext.Provider>
    </ErrorBoundary>
  )
})

const DarkTheme = React.lazy(() => import('./../themes/dark-mode'))

const LoggedInBase = observer(() => {
  const [state, store] = useLoggedInStore()

  useGlobalKeyDown({escape: store.onEscape})
  useLocationUpdateEffect(ChatUtils.onLocationChange)
  useCompanyUpdateEffect()

  useEffect(() => {
    ChatUtils.onLocationChange()
  }, [idTasksStore.state.modified])

  useEffect(() => {
    store.initialize()
    store.setPage()
    pendingCompaniesAdminStore.subscribe()

    return Storage.subscribe(store.syncBlockColumnSortWarning)
  }, [])

  if (!state.ready) {
    return null
  }

  return (
    <div
      id="loggedin-container"
      className={cn(state.scriptsOpen && 'scripting')}
    >
      {store.isPageWithBg && <Background />}

      <React.Suspense fallback={<></>}>
        {globals.theme === 'dark' && <DarkTheme />}
      </React.Suspense>

      <Scripts />
      {!globals.companyChanging && (
        <div
          className={cn(
            'loggedin-below',
            `loggedin-page--${state.page}`,
            'appear'
          )}
        >
          <MainMenu />
          <MainContentContainer />
          <RightPanel />
          <PendingCompanyDialog />
        </div>
      )}

      <PopupContainer />
      <UserCard />

      {taskConnectionStore.state.taskToConnect && (
        <div className="task-list-connect">
          <i className="fi-info task-list-connect-icon"></i>
          {Lang.msg.clickTaskConnect} “
          <span className="task-list-connect-with">
            {taskConnectionStore.state.taskToConnect.title}
          </span>
          ”
          <br />
          <span
            className="_link task-list-connect-cancel"
            onClick={store.connectionStoreClear}
          >
            {Lang.msg.cancel}
          </span>
        </div>
      )}
      <UiSlot name="root" />
    </div>
  )
})

const MainContentContainer = observer(() => {
  const [, store] = useLoggedInStore()

  return (
    <div className="flex flex-row flex-1 h-full">
      <div
        style={{
          flex: `0 0 ${store.leftMargin}`
        }}
      />
      <div className="cnt-main z-1 min-w-0 flex-1">
        <MainContent />
      </div>
      <div
        style={{
          flex: `0 0 ${store.rightMargin}`,
          transition: 'flex 0.4s cubic-bezier(0, 1, 0, 1)'
        }}
      />
    </div>
  )
})

const MainContent = observer(() => {
  const [state, store] = useLoggedInStore()

  return (
    <>
      {state.page === 'billing' && <Billing />}

      {store.isEmptySharing() && (
        <div className="py-64 text-center text-inverse-old text-24">
          {Lang.msg.share.nothingToShow}
        </div>
      )}

      {state.page === 'board' && <Project />}
      {state.page === 'feedback' && <FeedbackPage />}
      {state.page === 'statistics' && <Statistics page={state.subpage} />}
      {state.page === 'partner' && <PartnerPage />}
      <PageContainer />
    </>
  )
})
